import AsyncStorage from '@react-native-async-storage/async-storage';
import * as SecureStore from 'expo-secure-store';
import { useEffect, useRef, useState } from 'react';
import { Animated, Platform } from 'react-native';
import { useCheckIn, useSettings } from '..';
import { Alert } from '../../models/view';
import { useAppSelector } from '../../store';
import { makeCheckInAlert } from './ui/checkInAlert';
import { makePostAssessmentAlert } from './ui/postAssessmentAlert';
import { makePreAssessmentAlert } from './ui/preAssessmentAlert';
import { makeProgramSurveyAlert } from './ui/programSurveyAlert';
import { makeStudentCheckboxesAlert } from './ui/studentCheckboxesAlert';
import { makeVolunteerCheckboxesAlert } from './ui/volunteerCheckboxesAlert';
import {
  campaignIsMentor,
  campaignIsProtege,
  campaignIsStudents,
  campaignIsVolunteers,
  shouldShowBackgroundCheckAlert,
  shouldShowPostAssessmentAlert,
  shouldShowPreAssessmentAlert,
  shouldShowProgramSurveyAlert,
  shouldShowTrainingAlert,
  STUDENT_CHECKBOXES_DISMISSAL_KEY,
  todayIsFallEnrollmentPeriod,
  todayIsSpringEnrollmentPeriod,
  TRAINING_BACKGROUND_DISMISSAL_KEY,
  userIsMentorInterested,
  userIsStudent,
  userIsVolunteer,
} from './utils';

export const CHECKLIST_ITEM_BACKGROUND_CHECK = 'CHECKLIST_ITEM_BACKGROUND_CHECK';
export const CHECKLIST_ITEM_ENROLLED_FOR_FALL = 'CHECKLIST_ITEM_ENROLLED_FOR_FALL';
export const CHECKLIST_ITEM_ENROLLED_FOR_SPRING = 'CHECKLIST_ITEM_ENROLLED_FOR_SPRING';
export const CHECKLIST_ITEM_REGISTERED_WITH_CAREER_CENTER = 'CHECKLIST_ITEM_REGISTERED_WITH_CAREER_CENTER';
export const CHECKLIST_ITEM_TRAINING = 'CHECKLIST_ITEM_TRAINING';

export type ChecklistItem = {
  type: string;
  status: boolean;
};

export const useAlerts = () => {
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const campaign = useAppSelector((state) => state.campaigns.currentCampaign);
  const programEvents = [...useAppSelector((state) => state.programEvents.programEvents)];
  const { canCheckIn, currentActivity, handleCheckIn } = useCheckIn();
  const { registrationSubmitted } = useAppSelector((state) => state.registrations);
  const { getSettingValueByName } = useSettings();
  const [checkInClicked, setCheckInClicked] = useState(false);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [userHasDismissedVolunteerCheckboxesAlert, setUserHasDismissedVolunteerCheckboxesAlert] = useState(true);
  const [userHasDismissedStudentCheckboxesAlert, setUserHasDismissedStudentCheckboxesAlert] = useState(true);

  const fadeAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    getStoredStudentCheckboxesDismissalStatus();
    getStoredVolunteerCheckboxesDismissalStatus();
  }, []);

  useEffect(() => {
    const newAlerts: Alert[] = [];

    /**
     * If the user is not logged in, don't show any alerts
     */
    if (!currentUser) {
      setAlerts(newAlerts);
      return;
    }

    /**
     * Volunteer-specific alerts
     */

    if (userIsVolunteer(currentUser)) {
      const userIsInVolunteerCampaign = campaign && campaignIsVolunteers(campaign);
      const userIsInMentorCampaign = campaign && campaignIsMentor(campaign);
      const userShouldSeeBackgroundAlert = shouldShowBackgroundCheckAlert(currentUser.contact);
      const userShouldSeeTrainingAlert = shouldShowTrainingAlert(currentUser.contact);

      /**
       * Assemble items to be displayed in the checklist
       */
      const displayedChecklistItems: ChecklistItem[] = [];

      /**
       * Display the Fall checklist item if:
       *  - today is within the Fall enrollment period
       *  - AND
       *     - current user is eligible for reenrollment (Fall) (i.e. user should be shown the option to enroll)
       *     - OR
       *     - current user is already in a Volunteer ("Fall") campaign (i.e. user should be shown that they
       *       are already enrolled)
       */
      if (todayIsFallEnrollmentPeriod() && (currentUser.eligibleForReenrollment || userIsInVolunteerCampaign)) {
        displayedChecklistItems.push({
          type: CHECKLIST_ITEM_ENROLLED_FOR_FALL,
          status: true,
        });
      }

      /**
       * Display the Spring checklist item if:
       *  - today is within the Spring enrollment period
       *  - AND
       *     - current user is eligible for Spring enrollment (i.e. user should be shown the option to enroll)
       *     - OR
       *     - current user is already in a Mentor ("Spring") campaign (i.e. user should be shown that they
       *       are already enrolled)
       */
      if (todayIsSpringEnrollmentPeriod() && (currentUser.eligibleForSpringEnrollment || userIsInMentorCampaign)) {
        displayedChecklistItems.push({
          type: CHECKLIST_ITEM_ENROLLED_FOR_SPRING,
          status: true,
        });
      }

      /**
       * Always display the background check checklist item
       */
      displayedChecklistItems.push({
        type: CHECKLIST_ITEM_BACKGROUND_CHECK,
        status: true,
      });

      /**
       * Always display the training checklist item
       */
      displayedChecklistItems.push({
        type: CHECKLIST_ITEM_TRAINING,
        status: true,
      });

      let allChecklistItemsAreTrue = true;

      /**
       * Qualify each checklist item
       */
      displayedChecklistItems.forEach((item) => {
        if (item.type === CHECKLIST_ITEM_ENROLLED_FOR_FALL) {
          if (!userIsInVolunteerCampaign) {
            item.status = allChecklistItemsAreTrue = false;
          }
        }
        if (item.type === CHECKLIST_ITEM_ENROLLED_FOR_SPRING) {
          if (!userIsMentorInterested(currentUser)) {
            item.status = allChecklistItemsAreTrue = false;
          }
        }
        if (item.type === CHECKLIST_ITEM_BACKGROUND_CHECK) {
          if (userShouldSeeBackgroundAlert) {
            item.status = allChecklistItemsAreTrue = false;
          }
        }
        if (item.type === CHECKLIST_ITEM_TRAINING) {
          if (userShouldSeeTrainingAlert) {
            item.status = allChecklistItemsAreTrue = false;
          }
        }
      });

      /**
       * If any of the checklist items are false, reset the alert dismissal
       */
      if (!allChecklistItemsAreTrue) {
        setUserHasDismissedVolunteerCheckboxesAlert(false);
        handleResetVolunteerCheckboxesDismissalStatus();
      }

      /**
       * If the user has not dismissed the alert, show it
       */
      if (!userHasDismissedVolunteerCheckboxesAlert) {
        newAlerts.push(
          makeVolunteerCheckboxesAlert(
            handleDismissVolunteerCheckboxesAlert,
            fadeAnim,
            displayedChecklistItems,
            campaign,
            getSettingValueByName,
          ),
        );
      }
    }

    /**
     * Student-specific alerts
     */
    if (userIsStudent(currentUser)) {
      const userIsInStudentCampaign = campaign && campaignIsStudents(campaign);
      const userIsInProtegeCampaign = campaign && campaignIsProtege(campaign);

      /**
       * Assemble items to be displayed in the checklist
       */
      const displayedChecklistItems: ChecklistItem[] = [];

      /**
       * Display the Fall checklist item if:
       *  - today is within the Fall enrollment period
       *  - AND
       *     - current user is eligible for reenrollment (Fall) (i.e. user should be shown the option to enroll)
       *     - OR
       *     - current user is already in a Student ("Fall") campaign (i.e. user should be shown that they
       *       are already enrolled)
       */
      if (todayIsFallEnrollmentPeriod() && (currentUser.eligibleForReenrollment || userIsInStudentCampaign)) {
        displayedChecklistItems.push({
          type: CHECKLIST_ITEM_ENROLLED_FOR_FALL,
          status: true,
        });
      }

      /**
       * Display the Spring checklist item if:
       *  - today is within the Spring enrollment period
       *  - AND
       *     - current user is eligible for Spring enrollment (i.e. user should be shown the option to enroll)
       *     - OR
       *     - current user is already in a Protege ("Spring") campaign (i.e. user should be shown that they
       *       are already enrolled)
       */
      if (todayIsSpringEnrollmentPeriod() && (currentUser.eligibleForSpringEnrollment || userIsInProtegeCampaign)) {
        displayedChecklistItems.push({
          type: CHECKLIST_ITEM_ENROLLED_FOR_SPRING,
          status: true,
        });
      }

      displayedChecklistItems.push({
        type: CHECKLIST_ITEM_REGISTERED_WITH_CAREER_CENTER,
        status: true,
      });

      let allChecklistItemsAreTrue = true;

      /**
       * Qualify each checklist item
       */
      displayedChecklistItems.forEach((item) => {
        if (item.type === CHECKLIST_ITEM_ENROLLED_FOR_FALL) {
          if (!userIsInStudentCampaign) {
            item.status = allChecklistItemsAreTrue = false;
          }
        }
        if (item.type === CHECKLIST_ITEM_ENROLLED_FOR_SPRING) {
          if (!userIsMentorInterested(currentUser)) {
            item.status = allChecklistItemsAreTrue = false;
          }
        }
        if (item.type === CHECKLIST_ITEM_REGISTERED_WITH_CAREER_CENTER) {
          if (!currentUser.graduwayId) {
            item.status = allChecklistItemsAreTrue = false;
          }
        }
      });

      /**
       * If any of the checklist items are false, reset the alert dismissal
       */
      if (!allChecklistItemsAreTrue) {
        setUserHasDismissedStudentCheckboxesAlert(false);
        handleResetStudentCheckboxesDismissalStatus();
      }

      /**
       * If the user has not dismissed the alert, show it
       */
      if (!userHasDismissedStudentCheckboxesAlert) {
        newAlerts.push(
          makeStudentCheckboxesAlert(handleDismissStudentCheckboxesAlert, fadeAnim, displayedChecklistItems),
        );
      }

      // Student check-in
      if (campaign && canCheckIn && currentActivity) {
        newAlerts.push(makeCheckInAlert(currentActivity, handleCheckIn, setCheckInClicked, checkInClicked));
      }

      // Student pre-assessment; evaluates 1st event
      if (campaign && shouldShowPreAssessmentAlert(campaign, programEvents?.[0])) {
        newAlerts.push(makePreAssessmentAlert(campaign.preAssessmentLink));
      }

      // Student post-assessment; evaluates 4th event
      if (campaign && shouldShowPostAssessmentAlert(campaign, programEvents?.[3])) {
        newAlerts.push(makePostAssessmentAlert(campaign.postAssessmentLink));
      }
    }

    // End of campaign survey
    if (campaign && shouldShowProgramSurveyAlert(campaign)) {
      newAlerts.push(makeProgramSurveyAlert(campaign.endOfCourseSurvey));
    }

    setAlerts(newAlerts);
  }, [
    checkInClicked,
    currentUser,
    campaign,
    registrationSubmitted,
    userHasDismissedStudentCheckboxesAlert,
    userHasDismissedVolunteerCheckboxesAlert,
  ]);

  const getStoredVolunteerCheckboxesDismissalStatus = async () => {
    if (Platform.OS === 'web') {
      const val = await AsyncStorage.getItem(TRAINING_BACKGROUND_DISMISSAL_KEY);
      setUserHasDismissedVolunteerCheckboxesAlert(Boolean(val));
    } else {
      const val = await SecureStore.getItemAsync(TRAINING_BACKGROUND_DISMISSAL_KEY);
      setUserHasDismissedVolunteerCheckboxesAlert(Boolean(val));
    }
  };

  const getStoredStudentCheckboxesDismissalStatus = async () => {
    if (Platform.OS === 'web') {
      const val = await AsyncStorage.getItem(STUDENT_CHECKBOXES_DISMISSAL_KEY);
      setUserHasDismissedStudentCheckboxesAlert(Boolean(val));
    } else {
      const val = await SecureStore.getItemAsync(STUDENT_CHECKBOXES_DISMISSAL_KEY);
      setUserHasDismissedStudentCheckboxesAlert(Boolean(val));
    }
  };

  const handleDismissStudentCheckboxesAlert = () => {
    if (Platform.OS === 'web') {
      AsyncStorage.setItem(STUDENT_CHECKBOXES_DISMISSAL_KEY, 'true');
    } else {
      SecureStore.setItemAsync(STUDENT_CHECKBOXES_DISMISSAL_KEY, 'true');
    }

    setUserHasDismissedStudentCheckboxesAlert(true);
  };

  const handleDismissVolunteerCheckboxesAlert = () => {
    if (Platform.OS === 'web') {
      AsyncStorage.setItem(TRAINING_BACKGROUND_DISMISSAL_KEY, 'true');
    } else {
      SecureStore.setItemAsync(TRAINING_BACKGROUND_DISMISSAL_KEY, 'true');
    }

    setUserHasDismissedVolunteerCheckboxesAlert(true);
  };

  const handleResetVolunteerCheckboxesDismissalStatus = () => {
    if (Platform.OS === 'web') {
      AsyncStorage.removeItem(TRAINING_BACKGROUND_DISMISSAL_KEY);
    } else {
      SecureStore.deleteItemAsync(TRAINING_BACKGROUND_DISMISSAL_KEY);
    }
  };

  const handleResetStudentCheckboxesDismissalStatus = () => {
    if (Platform.OS === 'web') {
      AsyncStorage.removeItem(STUDENT_CHECKBOXES_DISMISSAL_KEY);
    } else {
      SecureStore.deleteItemAsync(STUDENT_CHECKBOXES_DISMISSAL_KEY);
    }
  };

  return { alerts };
};
